import { BRAND } from '@palaccount/config';
import type { MeResponse } from '@palaccount/contracts';
import { ChevronRight, Landmark, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { AnimatePresence, motion, type Transition } from 'motion/react';
import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';

import { NAV_GROUPS, type NavGroup } from '@/app/shell/nav';
import { RailTipContext, type RailTip } from '@/app/shell/rail-tip';
import { UserCard } from '@/app/shell/user-card';
import {
  hasModule,
  holds,
  holdsArea,
  useCapabilities,
  type SessionCapabilities,
} from '@/lib/permissions';
import { readNavPinned, writeNavPinned } from '@/lib/personalization';
import { useUiStore } from '@/lib/ui-store';
import { useMediaQuery } from '@/lib/use-viewport';
import { cn } from '@/lib/utils';

/**
 * BR-PERM-19 / BR-PERM-43 and §1.5.3, in one function.
 *
 * A group survives when the role holds SOMETHING in one of its areas AND the
 * company has its module. §1.5.2 fixes the order the two are asked in —
 * "permission is checked first, then the module flag" — which matters for what
 * a refused ROUTE says (see route-permissions.ts) and not for the menu, where
 * either answer produces the same absence.
 *
 * Exported so the menu and the tests can ask one question rather than two
 * implementations of it.
 */
export function visibleGroups(
  caps: SessionCapabilities,
  groups: readonly NavGroup[] = NAV_GROUPS,
): readonly NavGroup[] {
  return groups.filter((group) => {
    // Permission first (§1.5.2).
    const permitted =
      group.areas.length === 0 ||
      group.areas.some((area) => holdsArea(caps, area)) ||
      (group.extras?.some((permission) => holds(caps, permission)) ?? false);
    if (!permitted) return false;
    // Then the module. Note it is asked AFTER the owner short-circuit inside
    // holdsArea rather than through it: no role reaches an unbought module.
    return group.module === undefined || hasModule(caps, group.module);
  });
}

/**
 * THE RAIL AT REST: 10px of padding, a 56px icon cell, 10px of padding. The
 * cell is `w-14` on every row below and never changes width, so an icon sits
 * at the same x whether the rail is closed or open — the eye's anchor while
 * everything else moves.
 */
export const RAIL_WIDTH = 76;
/** The narrowest the OPEN rail may be, so the brand block and the user card keep their shape. */
export const MIN_OPEN_WIDTH = 240;
/** The widest. Past this a label ellipsizes rather than pushing the rail across the page. */
export const MAX_OPEN_WIDTH = 420;
/** What a row needs beyond its label: the chevron slot and the trailing padding. */
const ROW_TAIL = 48;
/** How long the rail stays open after the pointer leaves — long enough to skim the edge, too short to notice. */
const LEAVE_GRACE_MS = 160;

const SPRING: Transition = { type: 'spring', stiffness: 420, damping: 38, mass: 0.9 };
const INSTANT: Transition = { duration: 0 };

/**
 * The side menu of §4.3.1 — brand block (64px, matching the top bar) on top,
 * scrollable group list in the middle, the user card fixed below a hairline
 * (GEN-6). Two placements of one component, as since D25: `app-shell.tsx`
 * mounts it on a wide window and `nav-slide-over.tsx` inside a panel on a
 * narrow one. One menu, one permission filter; `visibleGroups` cannot come to
 * two answers on two window widths because there is only one of it.
 *
 * ── THE RAIL (owner ruling, Oct 2026, superseding SP-01's fixed column) ────
 *
 * The owner's words on the fixed 256px column: it "is always expanded, doesn't
 * shrink, and doesn't even fill the width of the text" — the longest English
 * label ellipsized inside it. So on the wide frame (`variant="rail"`, the
 * default) the menu is a RAIL:
 *
 *   - At rest it is `RAIL_WIDTH` (76px) of icons. Every label is still in the
 *     document — faded and nudged toward its icon, not removed — so a screen
 *     reader hears exactly the menu a sighted person sees when it opens, and
 *     every link also carries `aria-label` so its name never depends on a
 *     span's opacity.
 *   - It OPENS on pointer hover to a width MEASURED from the rendered labels
 *     (`scrollWidth` of each, in whichever language and text size is in
 *     force), clamped to [MIN_OPEN_WIDTH, MAX_OPEN_WIDTH]. Nothing is clipped
 *     to a guessed number, which is the defect being fixed.
 *   - Opening on hover OVERLAYS the page: the frame reserves 76px and the
 *     open rail paints over the content with a shadow, so nothing reflows
 *     under the pointer. PINNING it ("Keep the menu open", persisted per
 *     device by `lib/personalization.ts`) makes it take real width and the
 *     content reflows exactly once, animated.
 *   - KEYBOARD FOCUS DOES NOT OPEN IT, deliberately. Focus passes through
 *     these fifteen stops on every Tab journey from the top bar to the page;
 *     a panel that slid over the content each time would be the most
 *     frequent animation in the product and the least asked-for. A focused
 *     control instead shows its label in a bubble beside the rail (and the
 *     ring, as everywhere) — the rail that VS Code, Slack and GitLab draw.
 *     Escape dismisses the bubble without moving focus (WCAG 1.4.13); the pin
 *     button is itself a Tab stop, so a keyboard user who wants every label
 *     at once has it in one keystroke. Pointer-click focus shows nothing: the
 *     rail is already open under the pointer.
 *   - The active group carries a pill and an accent bar that MOVE between
 *     rows (`layoutId`) rather than appearing on the new one; its icon tints
 *     to the primary colour and its label goes semibold.
 *   - Everything animates through `motion/react` springs and CSS
 *     transitions only — no hand-written keyframe rule, no animation
 *     utility, no timer that repaints (GEN-188's three mechanisms,
 *     `flash-rate.test.ts`). Under
 *     `prefers-reduced-motion` every transition is instant and the stagger is
 *     gone; the widths are the same, they just arrive at once.
 *   - NFR-02: every offset is logical (`start-0`, `ms-2`, `border-e`), and
 *     the x-offset the labels and the bubble slide in from flips sign with
 *     the document direction, so Arabic mirrors the whole thing.
 *
 * The slide-over mounts `variant="panel"`: always open, filling its panel, no
 * pin, no bubbles — D25's contract on a narrow window is untouched.
 *
 * ── STILL TRUE, FROM EARLIER RULINGS ───────────────────────────────────────
 *
 * D25 (26 Aug 2026, reversible): GEN-1's "present on every screen after
 * sign-in" and §4.3.1's "never gets narrower and never disappears" hold on the
 * wide frame as PRESENCE — the rail is always there — and are deliberately not
 * held below the breakpoint, where the menu is reached through a button. The
 * width clause is now the owner's own ruling above.
 *
 * GEN-3/GEN-4 RECORDED DIVERGENCE: the prototype's group rows opened floating
 * hover panels with a pointer corridor; this build's group rows NAVIGATE to
 * the group's own landing screen, where the group's pages are a tab rail.
 * Same reachability, no hover physics on the rows themselves.
 *
 * GEN-2: the company under the product name is the signed-in user's tenant.
 * GEN-2a: the brand block opens the default landing screen and never leaves
 * the application.
 */
export function Sidebar({
  me,
  variant = 'rail',
}: {
  me: MeResponse;
  /** `rail` on the wide frame (default); `panel` inside the narrow slide-over. */
  variant?: 'rail' | 'panel';
}) {
  const { t } = useTranslation();
  const caps = useCapabilities();
  // GEN-162 / BR-PERM-19: the groups this person cannot view are ABSENT,
  // not greyed — a forbidden screen never appears in the menu at all.
  // BR-PERM-51: this is convenience only — every one of these routes enforces
  // its own permission on the server whether or not its entry was drawn.
  const groups = visibleGroups(caps);
  const rail = variant === 'rail';
  const railId = useId();

  // The one attribute that drives direction (ui-store.ts), read rather than
  // guessed: the labels slide in from the icon's side, which is a different
  // side in Arabic.
  const rtl = useUiStore((state) => state.locale === 'ar');
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  // A finger has no hover. On a wide touch screen a tap on an icon navigates
  // and the pin is the way to the labels — expanding on the synthetic
  // mouseenter a tap fires would leave the rail stuck open over the page.
  const noHover = useMediaQuery('(hover: none)');
  const transition = reduced ? INSTANT : SPRING;

  const [pinned, setPinned] = useState(() => rail && readNavPinned());
  const [hovered, setHovered] = useState(false);
  // The user card's menu is a portal outside this element, so the pointer
  // moving onto it is a mouseleave here. Hold the rail open while it is up.
  const [menuOpen, setMenuOpen] = useState(false);
  const expanded = !rail || pinned || hovered || menuOpen;

  const leaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  const onMouseEnter = () => {
    if (noHover) return;
    clearTimeout(leaveTimer.current);
    setHovered(true);
  };
  const onMouseLeave = () => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setHovered(false), LEAVE_GRACE_MS);
  };

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    writeNavPinned(next);
  };

  /*
   * THE OPEN WIDTH IS MEASURED, NOT CHOSEN. Each label is `whitespace-nowrap`
   * and clipped, so its `scrollWidth` is the width of its whole text in the
   * live font and text size (ADM-08 can change both). Re-measured when the
   * labels change (language), when the list's box changes (text size, font
   * load) and once the web fonts have settled. jsdom measures 0, and the floor
   * answers for it.
   */
  const labelRefs = useRef(new Map<string, HTMLElement>());
  const listRef = useRef<HTMLUListElement>(null);
  const [openWidth, setOpenWidth] = useState(MIN_OPEN_WIDTH);
  const measure = useCallback(() => {
    let widest = 0;
    for (const label of labelRefs.current.values()) widest = Math.max(widest, label.scrollWidth);
    setOpenWidth(Math.min(MAX_OPEN_WIDTH, Math.max(MIN_OPEN_WIDTH, RAIL_WIDTH + widest + ROW_TAIL)));
  }, []);
  const labelsKey = groups.map((group) => t(group.labelKey)).join('|');
  useLayoutEffect(() => {
    if (rail) measure();
  }, [rail, measure, labelsKey]);
  useEffect(() => {
    if (!rail) return;
    const list = listRef.current;
    if (list === null) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(list);
    if ('fonts' in document) void document.fonts.ready.then(measure);
    return () => observer.disconnect();
  }, [rail, measure]);

  /*
   * THE BUBBLE. Shown for a control that gained focus FROM THE KEYBOARD while
   * the rail is closed — the one case in which a label is both wanted and not
   * on screen. Modality is tracked the way `:focus-visible` is decided: the
   * last input before the focus event. Positioned against the rail's own box
   * so it sits beside the row even when the list has scrolled.
   */
  const asideRef = useRef<HTMLElement>(null);
  const keyboardRef = useRef(false);
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);
  useEffect(() => {
    if (!rail) return;
    const byKeyboard = () => {
      keyboardRef.current = true;
    };
    const byPointer = () => {
      keyboardRef.current = false;
    };
    document.addEventListener('keydown', byKeyboard, true);
    document.addEventListener('mousedown', byPointer, true);
    document.addEventListener('pointerdown', byPointer, true);
    document.addEventListener('touchstart', byPointer, true);
    return () => {
      document.removeEventListener('keydown', byKeyboard, true);
      document.removeEventListener('mousedown', byPointer, true);
      document.removeEventListener('pointerdown', byPointer, true);
      document.removeEventListener('touchstart', byPointer, true);
    };
  }, [rail]);
  useEffect(() => {
    if (expanded) setTip(null);
  }, [expanded]);
  const tipApi: RailTip | null = rail
    ? {
        onFocus: (label, anchor) => {
          if (expanded || !keyboardRef.current || asideRef.current === null) return;
          const frame = asideRef.current.getBoundingClientRect();
          const row = anchor.getBoundingClientRect();
          setTip({ label, top: row.top - frame.top + row.height / 2 });
        },
        onBlur: () => setTip(null),
      }
    : null;

  /** The x a label or bubble slides in from: toward the icon, which is the start side. */
  const slideFrom = (px: number) => (rtl ? px : -px);

  /** The shared recipe for a row's revealed text. */
  const reveal = (index: number) => ({
    initial: false as const,
    animate: { opacity: expanded ? 1 : 0, x: expanded ? 0 : slideFrom(12) },
    transition: reduced ? INSTANT : { ...SPRING, delay: expanded ? 0.025 * index : 0 },
  });

  const pinLabel = pinned ? t('shell.unpinMenu') : t('shell.pinMenu');
  const PinIcon = pinned ? PanelLeftClose : PanelLeftOpen;

  const content = (
    <RailTipContext.Provider value={tipApi}>
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <Link
          to="/"
          className="relative flex h-16 shrink-0 items-center border-b border-border px-2.5 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          onFocus={(event) => tipApi?.onFocus(BRAND.name, event.currentTarget)}
          onBlur={tipApi?.onBlur}
        >
          <span aria-hidden className="flex w-14 shrink-0 items-center justify-center">
            <span className="flex size-[34px] items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Landmark className="size-5" />
            </span>
          </span>
          <motion.span className="min-w-0 flex-1 pe-3" {...reveal(0)}>
            {/* R20: the name comes from the constant, never a literal. */}
            <span className="block truncate text-base font-bold leading-tight">{BRAND.name}</span>
            {/* dir=auto: a Latin company name keeps its own reading direction
                inside an RTL shell (and an Arabic one inside LTR) — without it
                the trailing period renders on the wrong side. */}
            <span
              dir="auto"
              className="block truncate text-[10px] font-medium uppercase tracking-widest text-muted-foreground"
            >
              {me.tenant.name}
            </span>
          </motion.span>
        </Link>

        {/* The list scrolls on a short window; the rail itself never does, and
            nothing in it may scroll sideways at any width. */}
        <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-2 [scrollbar-width:thin]">
          <ul ref={listRef} className="grid gap-1">
            {groups.map((group, index) => {
              const label = t(group.labelKey);
              return (
                <Fragment key={group.id}>
                  {group.startsSection && index > 0 ? (
                    <li role="separator" aria-hidden className="mx-2 my-1.5 border-t border-border" />
                  ) : null}
                  <li>
                    <NavLink
                      to={group.to}
                      end={group.to === '/'}
                      aria-label={label}
                      onFocus={(event) => tipApi?.onFocus(label, event.currentTarget)}
                      onBlur={tipApi?.onBlur}
                      className={({ isActive }) =>
                        cn(
                          'group/row relative flex h-10 items-center rounded-lg text-[15px] outline-none transition-colors',
                          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive ? (
                            <>
                              {/* The pill and the bar travel from the previous
                                  active row to this one — one element moving,
                                  not one vanishing and another appearing. */}
                              <motion.span
                                layoutId={`${railId}-pill`}
                                aria-hidden
                                className="absolute inset-0 rounded-lg bg-accent"
                                transition={transition}
                              />
                              <motion.span
                                layoutId={`${railId}-bar`}
                                aria-hidden
                                className="absolute inset-y-2 start-0 w-[3px] rounded-full bg-primary"
                                transition={transition}
                              />
                            </>
                          ) : null}
                          {/* The icon cell: fixed width, so the icon never moves
                              sideways while the rail opens. The lift on hover is
                              a CSS transition, stilled under reduced motion. */}
                          <span
                            aria-hidden
                            className={cn(
                              'relative flex w-14 shrink-0 items-center justify-center transition-transform duration-200 ease-out',
                              'group-hover/row:scale-110 group-active/row:scale-95 motion-reduce:transition-none motion-reduce:group-hover/row:scale-100',
                              isActive && 'text-primary',
                            )}
                          >
                            <group.icon className="size-5" />
                          </span>
                          <motion.span
                            ref={(element) => {
                              if (element) labelRefs.current.set(group.id, element);
                              else labelRefs.current.delete(group.id);
                            }}
                            className={cn('relative min-w-0 flex-1 truncate', isActive && 'font-semibold')}
                            {...reveal(index + 1)}
                          >
                            {label}
                          </motion.span>
                          {/* The trailing arrow of every group row; flips under RTL. */}
                          <motion.span
                            aria-hidden
                            className="relative me-3 shrink-0 rtl:rotate-180"
                            initial={false}
                            animate={{ opacity: expanded ? 0.5 : 0 }}
                            transition={transition}
                          >
                            <ChevronRight className="size-4" />
                          </motion.span>
                        </>
                      )}
                    </NavLink>
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </nav>

        {rail ? (
          <div className="shrink-0 px-2.5 pb-1.5">
            {/* GEN-182 / NFR-37: icon-only at rest, so it carries the name and
                the tooltip; `aria-expanded` says what it does to the rail it
                controls. */}
            <button
              type="button"
              onClick={togglePin}
              aria-expanded={pinned}
              aria-controls={railId}
              aria-label={pinLabel}
              title={pinLabel}
              onFocus={(event) => tipApi?.onFocus(pinLabel, event.currentTarget)}
              onBlur={tipApi?.onBlur}
              className={cn(
                'group/row relative flex h-10 w-full items-center rounded-lg text-sm text-muted-foreground outline-none transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
              )}
            >
              <span
                aria-hidden
                className="flex w-14 shrink-0 items-center justify-center transition-transform duration-200 ease-out group-hover/row:scale-110 motion-reduce:transition-none motion-reduce:group-hover/row:scale-100"
              >
                <PinIcon className="size-5 rtl:rotate-180" />
              </span>
              <motion.span
                className="min-w-0 flex-1 truncate pe-3 text-start"
                {...reveal(groups.length + 1)}
              >
                {pinLabel}
              </motion.span>
            </button>
          </div>
        ) : null}

        <UserCard me={me} expanded={expanded} onMenuOpenChange={setMenuOpen} />
      </div>
    </RailTipContext.Provider>
  );

  if (!rail) {
    return (
      <aside data-state="expanded" className="flex h-full w-full flex-col border-e border-border bg-card">
        {content}
      </aside>
    );
  }

  return (
    // The spacer: what the page is laid out against. 76px unless pinned, so
    // a hover never moves the content; pinning moves it once.
    <motion.div
      className="relative h-full shrink-0"
      initial={false}
      animate={{ width: pinned ? openWidth : RAIL_WIDTH }}
      transition={transition}
    >
      <motion.aside
        ref={asideRef}
        id={railId}
        data-testid="nav-rail"
        data-state={expanded ? 'expanded' : 'collapsed'}
        data-pinned={pinned ? 'true' : 'false'}
        data-motion={reduced ? 'reduced' : 'full'}
        aria-label={t('shell.mainMenu')}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setTip(null);
        }}
        className={cn(
          'absolute inset-y-0 start-0 z-30 border-e border-border bg-card transition-shadow duration-300',
          expanded && !pinned && 'shadow-xl',
        )}
        initial={false}
        animate={{ width: expanded ? openWidth : RAIL_WIDTH }}
        transition={transition}
      >
        {content}
        <AnimatePresence>
          {tip ? (
            <motion.span
              key="tip"
              role="tooltip"
              // The bubble repeats a name the control already carries, so it
              // is decoration to a screen reader and must not be read twice.
              aria-hidden
              className="pointer-events-none absolute start-full z-50 ms-2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-md"
              style={{ top: tip.top, y: '-50%' }}
              initial={{ opacity: 0, x: slideFrom(6), scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: slideFrom(4), scale: 0.98 }}
              transition={reduced ? INSTANT : { type: 'spring', stiffness: 520, damping: 34 }}
            >
              {tip.label}
            </motion.span>
          ) : null}
        </AnimatePresence>
      </motion.aside>
    </motion.div>
  );
}
